/**
 * In-app dice roller domain logic (task 20.3, R2.2 / R2.3).
 *
 * Rolling is a single action: the SPA calls the dice endpoint, the server rolls
 * via the shared true-RNG, records the roll in the campaign chat audit trail,
 * and returns the result the SPA displays (R2.2/R2.3). This module holds the
 * pure request-shaping so it is unit tested independently of React; the
 * component in components/DiceRoller.tsx renders the control and calls these.
 *
 * NOTE: an earlier "report a physical result" path (submitting the player's own
 * die total) was removed — the server never consumed the reported total (it
 * always re-rolled), so it silently overrode the player's die; a self-reported
 * roll that is quietly less trusted is a trust-eroding UX. A player who wants to
 * narrate a physical roll types it into chat.
 */

import type { DiceRollRequest } from "./api-client.js";

export interface RollContext {
  readonly campaignId: string;
  readonly characterId: string;
  /** Dice notation, e.g. "1d20". */
  readonly dice: string;
  readonly modifier?: number;
  readonly dc?: number;
}

/**
 * Build the dice-endpoint request from the roll context. The server rolls via
 * the shared true-RNG and records the result in the campaign chat audit trail
 * (R2.2/R2.3).
 */
export function buildDiceRequest(ctx: RollContext): DiceRollRequest {
  return {
    campaignId: ctx.campaignId,
    characterId: ctx.characterId,
    dice: ctx.dice,
    ...(ctx.modifier !== undefined ? { modifier: ctx.modifier } : {}),
    ...(ctx.dc !== undefined ? { dc: ctx.dc } : {}),
  };
}

/**
 * Build the short, player-facing chat tag for a resolved roll, to be APPENDED
 * to the composer so the player can add their own words around it before
 * sending (R2.3 keeps the authoritative roll in the audit log; this tag is just
 * a human-readable breadcrumb in the player's own message).
 *
 * Format: `[<dice><±mod> roll of <total>]`. A leading `1` count is dropped
 * (`1d20` → `d20`) since that is how players say it; a multi-die count is kept
 * (`2d6`). The modifier is shown when non-zero so the total reads
 * self-explanatory (`d20+3 roll of 18`).
 *
 * Examples:
 *   1d20, total 5           → "[d20 roll of 5]"
 *   1d20 +3, total 18       → "[d20+3 roll of 18]"
 *   2d6, total 8            → "[2d6 roll of 8]"
 */
export function rollChatTag(
  req: Pick<DiceRollRequest, "dice" | "modifier">,
  total: number,
): string {
  const parsed = parseDiceNotation(req.dice);
  // Normalize the notation: drop a leading count of 1 ("1d20" → "d20"); keep the
  // raw string if it did not parse (defensive — the server still has the truth).
  const notation =
    parsed && parsed.count === 1 ? `d${parsed.sides}` : (parsed ? `${parsed.count}d${parsed.sides}` : req.dice.trim());
  const mod = req.modifier ?? 0;
  const modStr = mod > 0 ? `+${mod}` : mod < 0 ? `${mod}` : "";
  return `[${notation}${modStr} roll of ${total}]`;
}

/** Parse a raw dice-notation string like "2d6" into `{ count, sides }`. */
export function parseDiceNotation(
  notation: string,
): { readonly count: number; readonly sides: number } | null {
  const m = /^(\d*)d(\d+)$/i.exec(notation.trim());
  if (!m) return null;
  const count = m[1] === "" ? 1 : Number.parseInt(m[1], 10);
  const sides = Number.parseInt(m[2], 10);
  if (count < 1 || sides < 1) return null;
  return { count, sides };
}
